// formula-debug.js — Formula Debug panel markup builder and live calculator.
// Extracted from script.js (lines 13589–13936).
// fdbgRecalc is assigned to window.fdbgRecalc so HTML onchange/oninput attrs still work.

import { effortConfig, blockHours, BLOCK_TABLE_REGISTRY } from './config.js';
import { sites } from './state.js';

// --- Block Tables — driven entirely by BLOCK_TABLE_REGISTRY in config.js ---
// Pass debugData (d) to get active-row highlighting based on live estimate values.
// Call without arguments (or with null) to render the static reference tables only.
export function buildBlockTablesMarkup(d) {
    function blockCard(entry) {
        const { title, color, metric, blocks, inputKey, baseline } = entry;

        // Resolve the current input value from debugData (if available)
        const inputVal = (d && inputKey != null && d[inputKey] != null)
            ? Number(d[inputKey]) : null;

        // Find which row the current input lands on
        let activeIdx = -1;
        if (inputVal != null && inputVal > 0) {
            const capped = Math.min(Math.max(0, inputVal), blocks[blocks.length - 1].max);
            activeIdx = blocks.findIndex(b => capped <= b.max);
            if (activeIdx === -1) activeIdx = blocks.length - 1;
        }

        const activeBlock  = activeIdx >= 0 ? blocks[activeIdx] : null;
        const activeOutput = activeBlock
            ? (baseline != null ? baseline + activeBlock.hours : activeBlock.hours)
            : null;

        // Callout strip: "current: X → Yh" or static label for per-scope tables
        let callout = '';
        if (inputKey === null) {
            callout = `<div class="fdbg-block-callout fdbg-block-callout--na">per-scope — see Decision Map for live values</div>`;
        } else if (inputVal != null) {
            const outputLabel = activeOutput != null ? `${activeOutput}h` : '0h';
            callout = `<div class="fdbg-block-callout">current: <strong>${inputVal}</strong> → <strong>${outputLabel}</strong></div>`;
        }

        // Build table rows
        const trs = blocks.map((b, i, arr) => {
            const lo = i === 0 ? 1 : arr[i - 1].max + 1;
            const range = `${lo}–${b.max}`;
            let hrsLabel, note;
            if (baseline != null) {
                hrsLabel = `${baseline}+${b.hours} = ${baseline + b.hours}h`;
                note = '';
            } else {
                hrsLabel = `${b.hours}h`;
                note = '';
            }
            const isActive = i === activeIdx;
            return `<tr class="fdbg-block-row${isActive ? ' fdbg-block-row--active' : ''}">
                <td class="fdbg-block-range">${range}</td>
                <td class="fdbg-block-hrs">${hrsLabel}${note ? ` <span class="fdbg-block-note">${note}</span>` : ''}</td>
            </tr>`;
        }).join('');

        return `<div class="fdbg-block-card">
            <div class="fdbg-block-card-hdr" style="background:${color}">
                <span class="fdbg-block-card-title">${title}</span>
                <span class="fdbg-block-metric">${metric}</span>
            </div>
            ${callout}
            <table class="fdbg-block-tbl">${trs}</table>
        </div>`;
    }

    return `<div class="fdbg-block-grid">
        ${BLOCK_TABLE_REGISTRY.map(blockCard).join('')}
    </div>`;
}

// --- Default values for Live Calculator when no estimate data is available ---
const CALC_DEFAULTS = {
    effortTotalInstances:      50,
    effortSelectedScopeCount:  1,
    siteCount:                 1,
    implementationMode:        'veeam-ps',
    veeamOneRequired:          'no',
    veeamOneOptionCount:       0,
    vroRequired:               'no',
    sureBackupRequired:        'no',
    sureReplicaRequired:       'no',
    backupCopyRequired:        'no',
    replicationRequired:       'no',
    cdpReplicationRequired:    'no',
    tapeCalcValue:             'none',
    sopRequired:               'no',
    osPlatform:                'windows',
    distributedTopology:       false,
    enablementRequired:        'no',
    uatRequired:               'no',
    asBuiltRequired:           'no',
    drRunbookRequired:         'no',
    estimatedHours:            0,
    estimatedDays:             0,
    kickoffHours:              0,
    customerKickoffHours:      0,
    discoveryHours:            0,
    discoveryWorkloadCount:    0,
    vb365TotalConsumedTb:      0,
    discoveryWorkloadHours:    0,
    componentDiscoveryHours:   0,
    discoverySiteHours:        0,
    designHours:               0,
    _vbrDesignVms:             0,
    _vb365DesignVms:           0,
    _kastenDesignVms:          0,
    _vbfCloudDesignVms:        0,
    implementationHours:       0,
    veeamOneHours:             0,
    veeamOneOptionHours:       0,
    vroHours:                  0,
    sureBackupHours:           0,
    backupCopyHours:           0,
    replicationHours:          0,
    sureReplicaHours:          0,
    cdpReplicationHours:       0,
    tapeIntegrationHours:      0,
    newVbrInstallHours:        0,
    architecturePlatformHours: 0,
    vsaV13AdjustmentHours:     0,
    upgradeHealthCheckHours:   0,
    jobValidationHours:        0,
    upgradePreFlightHours:     0,
    upgradeComponentHours:     0,
    vbrRepoMigrationHours:     0,
    vb365RepoMigrationHours:   0,
    hypervisorMigrationHours:  0,
    enablementHours:           0,
    uatHours:                  0,
    asBuiltHours:              0,
    drRunbookHours:            0,
    sopHours:                  0,
    projectManagementHours:    0,
    contingencyHours:          0,
};

// --- Live Calculator (exported — usable without full debugData) ---
export function buildLiveCalculatorMarkup(d = CALC_DEFAULTS) {
    function sel(name, opts, current) {
        return `<select class="fdbg-calc-ctrl" data-fdbg="${name}" onchange="fdbgRecalc()">` +
            opts.map(([v, l]) => `<option value="${v}"${current === v ? ' selected' : ''}>${l}</option>`).join('') +
            `</select>`;
    }
    function num(name, val, min, max) {
        return `<input class="fdbg-calc-ctrl fdbg-calc-num" type="number" data-fdbg="${name}" value="${val}" min="${min}" max="${max}" oninput="fdbgRecalc()">`;
    }
    function cRow(label, ctrl) {
        return `<div class="fdbg-calc-row"><span class="fdbg-calc-lbl">${label}</span>${ctrl}</div>`;
    }
    function cSec(title, rows) {
        return `<div class="fdbg-calc-sec"><div class="fdbg-calc-sec-title">${title}</div>${rows}</div>`;
    }
    function bkRow(color, label, key, val) {
        const active = val > 0;
        return `<div class="fdbg-bkd-row${active ? '' : ' fdbg-bkd-row--zero'}">
            <span class="fdbg-bkd-dot" style="background:${color}"></span>
            <span class="fdbg-bkd-lbl">${label}</span>
            <span class="fdbg-bkd-hrs${active ? ' fdbg-bkd-hrs--active' : ''}" data-fdbg-out="${key}">${val}h</span>
        </div>`;
    }
    const ny = [['no','No'],['yes','Yes']];
    return `<div class="fdbg-calc">
        <div class="fdbg-calc-grid">
            <div class="fdbg-calc-col">
                ${cSec('Scope — Design (per product)', [
                    cRow('VBR VMs',           num('effort-vbr',      d._vbrDesignVms      ?? d.effortTotalInstances ?? 0, 0, 1000)),
                    cRow('VB365 users',       num('effort-vb365',    d._vb365DesignVms    ?? 0, 0, 1000)),
                    cRow('Kasten namespaces', num('effort-kasten',   d._kastenDesignVms   ?? 0, 0, 1000)),
                    cRow('VBforCloud VMs',    num('effort-vbfcloud', d._vbfCloudDesignVms ?? 0, 0, 1000)),
                ].join(''))}
                ${cSec('Scope — Other', [
                    cRow('Discovery instances (EE inventory + job VMs)', num('discovery', d.discoveryWorkloadCount, 0, 10000)),
                    cRow('Workload count (scoped)',                       num('scope',     d.effortSelectedScopeCount, 1, 50)),
                    cRow('Sites',                                         num('sites',     d.siteCount, 1, 20)),
                ].join(''))}
                ${cSec('Implementation', [
                    cRow('Delivery mode',              sel('impl',       [['veeam-ps','Standard Professional Services'],['none','Resident Enterprise Architect']], d.implementationMode)),
                    cRow('Advisory shadow (REA only)', sel('advisory',   ny, d.advisorySupportRequired ?? 'no')),
                    cRow('Delivery style',             sel('impl-style', [['skeleton','Skeleton jobs (−20% design, −15% impl)'],['white-glove','White-glove (full effort)']], d._implDeliveryStyle ?? 'skeleton')),
                ].join(''))}
                ${cSec('Monitoring &amp; Orchestration', [
                    cRow('Veeam ONE',        sel('vone',      ny, d.veeamOneRequired)),
                    cRow('VONE option count', num('vone-opts', d.veeamOneOptionCount, 0, 10)),
                    cRow('VRO',              sel('vro',       ny, d.vroRequired)),
                ].join(''))}
            </div>
            <div class="fdbg-calc-col">
                ${cSec('Protection Policies', [
                    cRow('Backup Verification', sel('sb',   ny, d.sureBackupRequired)),
                    cRow('Backup Copy',         sel('bc',   ny, d.backupCopyRequired)),
                    cRow('Replication',         sel('rep',  ny, d.replicationRequired)),
                    cRow('SureReplica',         sel('sr',   ny, d.sureReplicaRequired || 'no')),
                    cRow('CDP Replication',     sel('cdp',  ny, d.cdpReplicationRequired)),
                    cRow('Tape',                sel('tape', [['none','None'],['sas','SAS'],['fiber-channel','Fibre Channel']], d.tapeCalcValue)),
                ].join(''))}
                ${cSec('Architecture', [
                    cRow('New VBR on Linux (+4h)',     sel('linux', ny, d.osPlatform === 'linux' ? 'yes' : 'no')),
                    cRow('Distributed topology (+8h)', sel('dist',  ny, d.distributedTopology ? 'yes' : 'no')),
                ].join(''))}
                ${cSec('Post-Deploy', [
                    cRow('Enablement',     sel('en',  ny, d.enablementRequired)),
                    cRow('UAT',            sel('uat', ny, d.uatRequired)),
                    cRow('As-built docs',  sel('asb', ny, d.asBuiltRequired)),
                    cRow('DR runbook',     sel('dr',  ny, d.drRunbookRequired)),
                    cRow('SOPs',           sel('sop', ny, d.sopRequired || 'no')),
                ].join(''))}
            </div>
        </div>
        <div class="fdbg-calc-total-bar">
            <div>
                <span class="fdbg-calc-total-label">Sandboxed total (incl. PM + contingency)</span>
                <span class="fdbg-calc-total-value" data-fdbg-out="total">${d.estimatedHours}h</span>
            </div>
            <div style="text-align:right">
                <span class="fdbg-calc-total-label">Days (÷ 8)</span>
                <span class="fdbg-calc-total-days" data-fdbg-out="days">${d.estimatedDays}d</span>
            </div>
        </div>
        <div class="fdbg-calc-breakdown">
            ${bkRow('#1a7a9e','Kickoff (internal)',                 'kickoff',          d.kickoffHours)}
            ${bkRow('#1a7a9e','Kickoff (customer / external)',      'customer-kickoff', d.customerKickoffHours || 0)}
            ${bkRow('#1a7a9e','Discovery — workloads',             'discovery-wl',     d.discoveryWorkloadHours ?? 0)}
            ${bkRow('#1a7a9e','Discovery — components',            'discovery-comp',   d.componentDiscoveryHours ?? 0)}
            ${bkRow('#1a7a9e','Discovery — sites',                 'discovery-site',   d.discoverySiteHours ?? 0)}
            ${bkRow('#5c4fcf','Design — VBR',                      'design-vbr',      (d._vbrDesignVms      ?? 0) > 0 ? blockHours(effortConfig.designBlocksVbr,      d._vbrDesignVms      ?? 0) : 0)}
            ${bkRow('#5c4fcf','Design — VB365',                    'design-vb365',    (d._vb365DesignVms    ?? 0) > 0 ? blockHours(effortConfig.designBlocksVb365,    d._vb365DesignVms    ?? 0) : 0)}
            ${bkRow('#5c4fcf','Design — Kasten K10',               'design-kasten',   (d._kastenDesignVms   ?? 0) > 0 ? blockHours(effortConfig.designBlocksKasten,   d._kastenDesignVms   ?? 0) : 0)}
            ${bkRow('#5c4fcf','Design — VBforCloud',               'design-vbfcloud', (d._vbfCloudDesignVms ?? 0) > 0 ? blockHours(effortConfig.designBlocksVbfCloud, d._vbfCloudDesignVms ?? 0) : 0)}
            ${bkRow('#5c4fcf','Design — VONE',                     'design-vone',     (d._voneDesignVms     ?? 0) > 0 ? blockHours(effortConfig.designBlocksVone,     d._voneDesignVms     ?? 0) : 0)}
            ${bkRow('#5c4fcf','Design — VRO',                      'design-vro',      (d._vroDesignVms      ?? 0) > 0 ? blockHours(effortConfig.designBlocksVro,      d._vroDesignVms      ?? 0) : 0)}
            ${bkRow('#0a8754','Implementation (backup jobs)',       'impl',            d.implementationHours)}
            ${bkRow('#0a8754','Implementation advisory (REA)',      'advisory',        d.advisoryHours ?? 0)}
            ${bkRow('#0a8754','Veeam ONE deployment',              'vone',            d.veeamOneHours + d.veeamOneOptionHours)}
            ${bkRow('#0a8754','VRO (recovery orchestration)',       'vro',             d.vroHours)}
            ${bkRow('#0a8754','Backup Verification',               'bv-sb',           d.sureBackupHours)}
            ${bkRow('#0a8754','Backup Copy',                       'bc',              d.backupCopyHours)}
            ${bkRow('#0a8754','Replication + SureReplica + CDP',   'rep',             d.replicationHours + d.cdpReplicationHours + (d.sureReplicaHours || 0))}
            ${bkRow('#0a8754','Tape Integration',                  'tape',            d.tapeIntegrationHours)}
            ${bkRow('#0a8754','New VBR install adj.',              'newvbr',          d.newVbrInstallHours)}
            ${bkRow('#0a8754','Architecture adj.',                 'arch',            d.architecturePlatformHours + d.vsaV13AdjustmentHours)}
            ${bkRow('#0a8754','Upgrade / HC',                      'upgrade',         (d.upgradeHealthCheckHours || 0) + (d.jobValidationHours || 0) + (d.upgradePreFlightHours || 0) + (d.upgradeComponentHours || 0))}
            ${bkRow('#1a7a5e','Migration',                         'migration',       (d.vbrRepoMigrationHours || 0) + (d.vb365RepoMigrationHours || 0) + (d.hypervisorMigrationHours || 0))}
            ${bkRow('#6a8f3c','Post-Deploy',                       'postdep',         d.enablementHours + d.uatHours + d.asBuiltHours + d.drRunbookHours + (d.sopHours || 0))}
            ${bkRow('#7a7a7a','Project Management',                'pm',              d.projectManagementHours || 0)}
            ${bkRow('#7a7a7a','Contingency',                       'conty',           d.contingencyHours || 0)}
        </div>
        <p class="fdbg-calc-note">Sandbox — changes here don't affect the live estimate above</p>
        <p class="fdbg-calc-note" style="margin-top:2px">⚠ Not modellable here: new VBR install (virtual/physical), HC/upgrade, VBR/VB365/HVM migrations — see Decision Map for live values</p>
    </div>`;
}

// --- Formula Debug panel builder ---
export function buildFormulaDebugPanelMarkup(d) {

    // ── Shared: category badge ───────────────────────────────────────────────
    const CAT = {
        base:      { label: 'Base',          color: '#2d7dd2' },
        discovery: { label: 'Discovery',     color: '#1a7a9e' },
        design:    { label: 'Design',        color: '#5c4fcf' },
        impl:    { label: 'Implementation', color: '#0a8754' },
        skills:  { label: 'Skills',        color: '#20a4a4' },
        postdep: { label: 'Post-Deploy',   color: '#6a8f3c' },
        adjust:  { label: 'Adjustments',   color: '#7a7a7a' },
        migration: { label: 'Migration',   color: '#1a7a5e' },
    };
    function badge(cat) {
        const c = CAT[cat];
        return `<span class="fdt__cat-badge" style="background:${c.color}">${c.label}</span>`;
    }

    // ── TAB 1: Decision Map ──────────────────────────────────────────────────
    function mapRow(cat, name, formula, hours) {
        const active = hours > 0;
        return `<tr class="fdt__row${active ? '' : ' fdt__row--zero'}">
            <td class="fdt__cat">${badge(cat)}</td>
            <td class="fdt__name">${name}</td>
            <td class="fdt__formula">${formula}</td>
            <td class="fdt__hrs ${active ? 'fdt__hrs--active' : 'fdt__hrs--zero'}">${hours}h</td>
        </tr>`;
    }
    const mapRows = [
        mapRow('discovery', 'Internal project kick-off (flat)',           'kickoffHours = 2',                                                                                  d.kickoffHours),
        mapRow('discovery', 'Customer (external) project kick-off (flat)','customerKickoffHours = 2',                                                                          d.customerKickoffHours || 0),
        mapRow('discovery', 'Discovery — workloads',   `blockHours(discoveryBlocks, ${d.discoveryWorkloadCount ?? 0} workloads + job VMs)`,                                          d.discoveryWorkloadHours ?? 0),
        mapRow('discovery', 'Discovery — components',  `direct rate × count per component type (VBR×${effortConfig.discoveryComponent.vbrServer}h, VBR proxy×${effortConfig.discoveryComponent.vbrProxy}h, VBR repo×${effortConfig.discoveryComponent.vbrRepository}h, VB365×${effortConfig.discoveryComponent.vb365Server}h, VB365 proxy×${effortConfig.discoveryComponent.vb365Proxy}h, VB365 repo×${effortConfig.discoveryComponent.vb365Repository}h, VBfCloud×${effortConfig.discoveryComponent.vbfCloudServer}h, VBfCloud repo×${effortConfig.discoveryComponent.vbfCloudRepository}h, Kasten×${effortConfig.discoveryComponent.kastenServer}h)`,                                          d.componentDiscoveryHours ?? 0),
        mapRow('discovery', 'Discovery — sites',       `${d.siteCount ?? 1} site${(d.siteCount ?? 1) !== 1 ? 's' : ''} × ${effortConfig.discoveryPerSite}h`,                                                      d.discoverySiteHours ?? 0),
        mapRow('design',  'Design — VBR',           `4h base + blockHours(designBlocksVbr, ${d._vbrDesignVms ?? 0} VMs = unprotected + job VMs)`,      (d._vbrDesignVms      ?? 0) > 0 ? 4 + blockHours(effortConfig.designBlocksVbr,      d._vbrDesignVms ?? 0)      : 0),
        mapRow('design',  'Design — VB365',         `4h base + blockHours(designBlocksVb365, ${d._vb365DesignVms ?? 0} users)`,  (d._vb365DesignVms    ?? 0) > 0 ? 4 + blockHours(effortConfig.designBlocksVb365,    d._vb365DesignVms ?? 0)    : 0),
        mapRow('design',  'Design — Kasten K10',    `4h base + blockHours(designBlocksKasten, ${d._kastenDesignVms ?? 0} ns)`,   (d._kastenDesignVms   ?? 0) > 0 ? 4 + blockHours(effortConfig.designBlocksKasten,   d._kastenDesignVms ?? 0)   : 0),
        mapRow('design',  'Design — VBforCloud',    `4h base + blockHours(designBlocksVbfCloud, ${d._vbfCloudDesignVms ?? 0} VMs)`, (d._vbfCloudDesignVms ?? 0) > 0 ? 4 + blockHours(effortConfig.designBlocksVbfCloud, d._vbfCloudDesignVms ?? 0) : 0),
        mapRow('design',  'Design — VONE',          `2h base + blockHours(designBlocksVone, ${d._voneDesignVms ?? 0} monitored VMs) — only when VONE required`, d._voneDesignHours ?? 0),
        mapRow('design',  'Design — VRO',           `4h base + blockHours(designBlocksVro, ${d._vroDesignVms ?? 0} orchestrated VMs) — only when VRO required`, d._vroDesignHours ?? 0),
        mapRow('design',  'Design — VBR repo migration',  `blockHours(repoMigrationDesignBlocks, ${d.vbrRepoMigrationTotalTb ?? 0} TB)`,  d.vbrRepoMigrationDesignHours  ?? 0),
        mapRow('design',  'Design — VB365 repo migration', `blockHours(repoMigrationDesignBlocks, ${d.vb365TotalConsumedTb ?? 0} TB)`,      d.vb365RepoMigrationDesignHours ?? 0),
        mapRow('design',  'Design — delivery style multiplier', `×${d._designEffortMultiplier ?? 1.0} ${d._templateJobsActive ? '(skeleton — design reduced 20%)' : '(white-glove / REA — full design effort)'}`, 0),
        mapRow('impl',    'Implementation',         d.implementationMode === 'none' ? 'REA – Advisory Only → 0h (all impl hours suppressed)'
                                                        : `blockHours per workload${d._implDeliveryStyle === 'skeleton' ? ' ×0.85 (skeleton jobs −15%)' : ' (white-glove — full effort)'}`,                                               d.implementationHours),
        mapRow('impl',    'Veeam ONE',              `8h baseline + (${d.effortSelectedScopeCount} workloads × 4h)`,                                                           d.veeamOneHours),
        mapRow('impl',    'VONE Custom Options',    `${d.veeamOneOptionCount} selected options × 8h`,                                                                         d.veeamOneOptionHours),
        mapRow('impl',    'VRO',                    `8h baseline + (${d.effortSelectedScopeCount} workloads × 2h)`,                                                           d.vroHours),
        mapRow('impl',    'Backup Verification',    `${effortConfig.sureBackup.flat}h per unprotected workload with Verification selected in Step 2 matrix`,                  d.sureBackupHours),
        mapRow('impl',    'Backup Copy',            '4h baseline + blockHours(backupCopyBlocks, VM count) per unprotected workload with Backup Copy selected',               d.backupCopyHours),
        mapRow('impl',    'Replication',            'blockHours(replicationBlocks, VM count) per unprotected workload with Replication selected',                             d.replicationHours),
        mapRow('impl',    'SureReplica',            'flat 8h when replication is in scope',                                                                                  d.sureReplicaHours),
        mapRow('impl',    'CDP Replication',        'blockHours(cdpBlocks, VM count) per unprotected workload with CDP selected in Step 2 matrix',                           d.cdpReplicationHours),
        mapRow('impl',    'Tape Integration',       `${d.tapeConnectivityType}: SAS = 8h / Fibre Channel = 12h`,                                                             d.tapeIntegrationHours),
        mapRow('impl', 'Upgrade Health Check',   '16h flat baseline (prep, scripting, report) when HC active — absorbs pre-flight',                                       d.upgradeHealthCheckHours),
        mapRow('impl', 'Job Validation',         `blockHours(jobValidationBlocks, ${d.totalHcUnits ?? 0} blended HC units) — HC active only`,                             d.jobValidationHours),
        mapRow('impl', 'Upgrade Pre-flight',     `blockHours(upgradePreFlightBlocks, ${d.totalHcUnits ?? 0} HC units) — upgrade active, no HC; HC absorbs when both`,    d.upgradePreFlightHours),
        mapRow('impl', 'Upgrade Components',     `VBR: ${d.upgradeVbrCount ?? 0}×8h · VBR Proxies: ${d.upgradeProxyCount ?? 0}×2h · VBR Repos: ${d.upgradeRepoCount ?? 0}×2h · VB365 Proxies: ${d.upgradeVb365ProxyCount ?? 0}×2h · VB365 Repos: ${d.upgradeVb365RepoCount ?? 0}×2h · VBfCloud Repos: ${d.upgradeVbfCloudRepoCount ?? 0}×2h · VBfAWS: ${d.upgradeVbfAwsCount ?? 0}×4h · VBfAz: ${d.upgradeVbfAzCount ?? 0}×4h · VBfGC: ${d.upgradeVbfGcCount ?? 0}×4h · Kasten: ${d.upgradeKastenCount ?? 0}×4h`, d.upgradeComponentHours),
        mapRow('impl', 'VSA v13 Adjustment',     'veeamVersion ≠ v13 → +4h',                                                                                             d.vsaV13AdjustmentHours),
        mapRow('impl', 'New VBR Install',        'virtual=+4h · physical=+8h · Linux VSA=+4h — per new VBR lane (or implied 1× when no lanes)',                          d.newVbrInstallHours),
        mapRow('impl', 'Architecture Platform',  'distributed site topology = +8h',                                                                                       d.architecturePlatformHours),
        mapRow('impl', 'Distributed Deployment', 'proxies × 2h + repositories × 2h (⚠ config defined but not yet wired)',                                                d.distributedDeploymentHours),
        mapRow('migration', 'VBR Repo Migration',     'per scope: blockHours(dataSizeBlocks, TB)',                                                                              d.vbrRepoMigrationHours  ?? 0),
        mapRow('migration', 'VB365 Repo Migration',   `blockHours(dataSizeBlocks, ${d.vb365TotalConsumedTb ?? 0} TB)`,                                                       d.vb365RepoMigrationHours ?? 0),
        mapRow('migration', 'Hypervisor Migration',   'hvmEnabled: Σ scopes (4h decommission + blockHours(implBlocks, targetVMs) + 8h proxyReconfig? + 8h restoreTesting?)',  d.hypervisorMigrationHours),
        mapRow('postdep', 'Skills Transfer Workshop', 'flat 6h when enabled',                                                                                               d.enablementHours),
        mapRow('postdep', 'UAT',                    `${d.effortSelectedScopeCount} workloads × 8h`,                                                                          d.uatHours),
        mapRow('postdep', 'As-Built Documentation', 'flat 8h',                                                                                                               d.asBuiltHours),
        mapRow('postdep', 'DR Runbook',             'flat 8h',                                                                                                               d.drRunbookHours),
        mapRow('postdep', 'SOPs',                   'flat 8h when Standard Operating Procedures are required',                                                               d.sopHours || 0),
        mapRow('adjust',  'Project Management',     `10% of activity subtotal`,                                                                                               d.projectManagementHours || 0),
        mapRow('adjust',  'Contingency',            `10% of activity subtotal (risk buffer)`,                                                                                 d.contingencyHours || 0),
    ].join('');
    const totalRaw = Number(d.baseHours).toFixed(1);
    const decisionMapHtml = `
        <table class="fdbg-table">
            <thead><tr>
                <th class="fdt__th fdt__th--cat">Category</th>
                <th class="fdt__th fdt__th--name">Component</th>
                <th class="fdt__th fdt__th--formula">Formula / Condition</th>
                <th class="fdt__th fdt__th--hrs">Hours</th>
            </tr></thead>
            <tbody>${mapRows}</tbody>
            <tfoot><tr class="fdt__total-row">
                <td colspan="3" class="fdt__total-label">Total (pre-round → rounded → days)</td>
                <td class="fdt__total-hrs">${totalRaw}h &rarr; ${d.estimatedHours}h &rarr; ${d.estimatedDays}d</td>
            </tr></tfoot>
        </table>`;

    // ── TAB 2: Block Tables ──────────────────────────────────────────────────
    // Pass live debugData so active rows are highlighted
    const blockTablesHtml = buildBlockTablesMarkup(d);

    // ── TAB 3: Live Calculator ───────────────────────────────────────────────
    const calcHtml = buildLiveCalculatorMarkup(d);

    // ── Assemble ─────────────────────────────────────────────────────────────
    return `
        <section class="formula-debug-panel">
            <details open>
                <summary class="fdbg-header">
                    <span class="fdbg-badge">⚙ DEV</span>
                    <span class="fdbg-title">Formula Debug</span>
                    <span class="fdbg-meta">
                        effort units: <strong>${d.effortTotalInstances}</strong>
                        &middot; scope: <strong>${d.effortSelectedScopeCount}</strong>
                        &middot; sites: <strong>${d.siteCount}</strong>
                        &middot; hW: <strong>${d.hW ? '✓' : '–'}</strong>
                        &middot; hC: <strong>${d.hC ? '✓' : '–'}</strong>
                        &middot; impl: <strong>${d.implementationMode}</strong>
                        &middot; <em>triple-click version to hide</em>
                    </span>
                    <span class="fdbg-collapse-hint"></span>
                </summary>
                <div class="fdbg-body">
                    <input type="radio" id="fdbg-t1" name="fdbg-tabs" class="fdbg-radio" checked>
                    <input type="radio" id="fdbg-t2" name="fdbg-tabs" class="fdbg-radio">
                    <input type="radio" id="fdbg-t3" name="fdbg-tabs" class="fdbg-radio">
                    <div class="fdbg-tab-bar">
                        <label class="fdbg-tab-label" for="fdbg-t1">Decision Map</label>
                        <label class="fdbg-tab-label" for="fdbg-t2">Block Tables</label>
                        <label class="fdbg-tab-label" for="fdbg-t3">Live Calculator</label>
                    </div>
                    <div class="fdbg-panel fdbg-panel--map">${decisionMapHtml}</div>
                    <div class="fdbg-panel fdbg-panel--blocks">${blockTablesHtml}</div>
                    <div class="fdbg-panel fdbg-panel--calc">${calcHtml}</div>
                </div>
            </details>
        </section>`;
}
// --- End Formula Debug panel builder ---

// --- Formula Debug live calculator recalc (global, survives innerHTML re-renders) ---
window.fdbgRecalc = function () {
    const panel = document.querySelector('.fdbg-panel--calc');
    if (!panel) return;

    function v(k) { const el = panel.querySelector('[data-fdbg="' + k + '"]'); return el ? el.value : ''; }
    function n(k) { return Math.max(0, parseInt(v(k), 10) || 0); }

    const efVbr      = n('effort-vbr');
    const efVb365    = n('effort-vb365');
    const efKasten   = n('effort-kasten');
    const efVbfCloud = n('effort-vbfcloud');
    const ef         = Math.max(efVbr + efVb365 + efKasten + efVbfCloud, 1); // total for impl/skills/protection blocks
    const sc       = Math.max(1, n('scope'));
    const sites    = Math.max(1, n('sites'));
    const impl     = v('impl');
    const advisory = v('advisory');
    const vone     = v('vone');
    const voneOpts = n('vone-opts');
    const vro      = v('vro');
    const sb       = v('sb');
    const sr       = v('sr');
    const bc       = v('bc');
    const rep      = v('rep');
    const cdp      = v('cdp');
    const tape     = v('tape');
    const linux    = v('linux') === 'yes';
    const dist     = v('dist') === 'yes';
    const en       = v('en');
    const uat      = v('uat');
    const asb      = v('asb');
    const dr       = v('dr');
    const sop      = v('sop');

    const cfg = effortConfig;

    // ── Core ─────────────────────────────────────────────────────────────────
    const discoveryInst = parseInt(panel.querySelector('[data-fdbg="discovery"]')?.value, 10) || 0;
    const kickoffH         = cfg.baselineHours.kickoff;
    const customerKickoffH = cfg.baselineHours.customerKickoff;
    const discoveryWorkloadH = blockHours(cfg.discoveryBlocks, discoveryInst);
    const discoverySiteH = sites * cfg.discoveryPerSite;
    const discoveryH = discoveryWorkloadH + discoverySiteH; // note: component discovery hours not modelled in sandbox
    const implStyle       = impl === 'veeam-ps' ? (v('impl-style') ?? 'skeleton') : 'white-glove';
    const templateJobs    = implStyle === 'skeleton'; // skeleton always applies template design reduction
    const designMultiplier = templateJobs ? 0.8 : 1.0;
    const _db = 4; // 4h flat base per active product
    const designVbrH      = efVbr      > 0 ? _db + blockHours(cfg.designBlocksVbr,      efVbr)      : 0;
    const designVb365H    = efVb365    > 0 ? _db + blockHours(cfg.designBlocksVb365,    efVb365)    : 0;
    const designKastenH   = efKasten   > 0 ? _db + blockHours(cfg.designBlocksKasten,   efKasten)   : 0;
    const designVbfCloudH = efVbfCloud > 0 ? _db + blockHours(cfg.designBlocksVbfCloud, efVbfCloud) : 0;
    // Veeam ONE design — only when VONE required; scales with the monitored estate (discovery instances). 2h base.
    const designVoneH     = vone === 'yes' && discoveryInst > 0 ? 2 + blockHours(cfg.designBlocksVone, discoveryInst) : 0;
    // Veeam Recovery Orchestrator design — only when VRO required; scales with the orchestrated estate (discovery instances). 4h base.
    const designVroH      = vro === 'yes' && discoveryInst > 0 ? 4 + blockHours(cfg.designBlocksVro, discoveryInst) : 0;
    const designH         = Math.round((designVbrH + designVb365H + designKastenH + designVbfCloudH + designVoneH + designVroH) * designMultiplier);
    const fullImpl     = blockHours(cfg.implementationBlocks, ef);
    const implReduction = implStyle === 'skeleton' ? (1 - cfg.templateJobs.implReductionMultiplier) : 1.0;
    const implH        = impl === 'veeam-ps' ? Math.round(fullImpl * implReduction) : 0;
    const advisoryH = impl === 'none' && advisory === 'yes' ? Math.round(fullImpl * cfg.advisorySupport.multiplier) : 0;

    // ── Monitoring & Orchestration ───────────────────────────────────────────
    const voneH    = vone === 'yes' ? cfg.veeamOne.baseline + sc * cfg.veeamOne.perHypervisor : 0;
    const voneOptH = vone === 'yes' ? voneOpts * cfg.veeamOne.customOption : 0;
    const vroH     = vro  === 'yes' ? cfg.vro.baseline + sc * cfg.vro.perScopedItem : 0;

    // ── Protection ───────────────────────────────────────────────────────────
    const sbH   = sb === 'yes' ? cfg.sureBackup.flat : 0;
    const bcH   = bc  === 'yes' ? cfg.backupCopy.baseline + blockHours(cfg.backupCopy.blocks, ef) : 0;
    const repH  = rep === 'yes' ? blockHours(cfg.replicationBlocks, ef) : 0;
    const srH   = rep === 'yes' && sr === 'yes' ? cfg.sureReplica.flat : 0;
    const cdpH  = cdp === 'yes' ? blockHours(cfg.cdpReplicationBlocks, ef) : 0;
    const tapeH = tape === 'sas' ? cfg.tapeIntegration.sas
                : tape === 'fiber-channel' ? cfg.tapeIntegration['fiber-channel'] : 0;

    // ── Architecture ─────────────────────────────────────────────────────────
    const newvbrH = linux ? cfg.architectureAdjustments.linux : 0;
    const distH   = dist && sites > 1 ? cfg.architectureAdjustments.distributedSiteTopology : 0;

    // ── Post-Deploy ───────────────────────────────────────────────────────────
    const enH   = en  === 'yes' ? cfg.enablementHours.flat : 0;
    const uatH  = uat === 'yes' ? sc * cfg.uatHours.perHypervisor : 0;
    const asbH  = asb === 'yes' ? cfg.asBuiltDocumentation.flat : 0;
    const drH   = dr  === 'yes' ? cfg.drRunbook.flat : 0;
    const sopH  = sop === 'yes' ? cfg.sop.flat : 0;

    // ── Subtotal + overhead ───────────────────────────────────────────────────
    const archH      = distH;
    const postdepH   = enH + uatH + asbH + drH + sopH;
    const subTotal   = kickoffH + customerKickoffH + discoveryH + designH + implH + advisoryH
                     + voneH + voneOptH + vroH
                     + sbH + bcH + repH + srH + cdpH + tapeH + newvbrH + archH + postdepH;

    const pmH    = Math.round(subTotal * cfg.projectManagement.rate);
    const contyH = Math.round(subTotal * cfg.contingency.rate);
    const total  = subTotal + pmH + contyH;

    function setOut(key, text) {
        panel.querySelectorAll('[data-fdbg-out="' + key + '"]').forEach(el => {
            el.textContent = text;
            const row = el.closest('.fdbg-bkd-row');
            if (row) {
                const hrs = parseFloat(text) || 0;
                row.classList.toggle('fdbg-bkd-row--zero', hrs === 0);
                el.classList.toggle('fdbg-bkd-hrs--active', hrs > 0);
            }
        });
    }

    setOut('kickoff',          kickoffH + 'h');
    setOut('customer-kickoff', customerKickoffH + 'h');
    setOut('discovery',        discoveryH + 'h');
    setOut('discovery-wl',     discoveryWorkloadH + 'h');
    setOut('discovery-site',   discoverySiteH + 'h');
    setOut('design-vbr',      designVbrH      + 'h');
    setOut('design-vb365',    designVb365H    + 'h');
    setOut('design-kasten',   designKastenH   + 'h');
    setOut('design-vbfcloud', designVbfCloudH + 'h');
    setOut('design-vone',     designVoneH     + 'h');
    setOut('design-vro',      designVroH      + 'h');
    setOut('impl',             implH + 'h');
    setOut('advisory',         advisoryH + 'h');
    setOut('vone',             (voneH + voneOptH) + 'h');
    setOut('vro',              vroH + 'h');
    setOut('bv-sb',            sbH + 'h');
    setOut('bc',               bcH + 'h');
    setOut('rep',              (repH + srH + cdpH) + 'h');
    setOut('tape',             tapeH + 'h');
    setOut('newvbr',           newvbrH + 'h');
    setOut('arch',             archH + 'h');
    setOut('postdep',          postdepH + 'h');
    setOut('pm',               pmH + 'h');
    setOut('conty',            contyH + 'h');
    setOut('total',            total + 'h');
    setOut('days',             Math.ceil(total / 8) + 'd');
};
// --- End Formula Debug live calculator ---
